/**
 * Transaction Categorise Modal Component
 * 
 * Modal for categorising a single transaction
 */

import React, { useState, useEffect, useMemo } from 'react';
import { X, Search, CheckCircle, AlertCircle, Loader2, Sparkles, ChevronRight, ChevronDown } from 'lucide-react';
import { useCategoriseTransaction, useUpdateTransaction } from '../../hooks/useTransactions';
import { categoryService } from '../../services/category-service';
import { transactionService } from '../../services/transaction-service';
import { hybridAccountService } from '../../services/hybrid-account-service';
import { getCurrentUserId } from '../../lib/supabase';
import { Category, CategoryType } from '../../types/category.types';
import { Transaction } from '../../types/transaction.types';
import { Account } from '../../types/account.types';
import { getCategoryIconComponent } from '../../utils/icon-utils';

interface TransactionCategoriseModalProps {
  isOpen: boolean;
  transaction: Transaction | null;
  onClose: () => void;
  onComplete: () => void;
}

export function TransactionCategoriseModal({
  isOpen,
  transaction,
  onClose,
  onComplete
}: TransactionCategoriseModalProps): React.ReactElement | null {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [account, setAccount] = useState<Account | null>(null);
  const [_loadingAccount, setLoadingAccount] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);
  const [suggestions, setSuggestions] = useState<Array<{ categoryId: string; categoryName: string; count: number }>>([]);
  const [_loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [expandedCategoryIds, setExpandedCategoryIds] = useState<Set<string>>(new Set());
  const [typeFilter, setTypeFilter] = useState<'all' | CategoryType>('all');
  const [notes, setNotes] = useState<string>('');

  const categoriseMutation = useCategoriseTransaction();
  const updateMutation = useUpdateTransaction();

  // Determine category type filter based on transaction type
  const categoryType = useMemo(() => {
    if (!transaction) return null;
    // Debit transactions should show expense AND transfer categories (e.g., credit card payments are transfers).
    // Credit transactions need to show all categories (income, expense, transfer),
    // so return null to avoid filtering by type.
    return transaction.type === 'debit' ? [CategoryType.EXPENSE, CategoryType.TRANSFER] : null;
  }, [transaction]);

  // Fetch categories when modal opens
  useEffect(() => {
    if (!isOpen || !transaction) return;

    const loadCategories = async () => {
      setLoadingCategories(true);
      setError(null);
      try {
        // Always fetch all categories and let the UI filter handle filtering
        // This ensures all category types are available when the user selects them in the filter
        const params: any = {};
        
        const result = await categoryService.listCategories(params);
        if (result.error) {
          setError(result.error.message);
          setCategories([]);
        } else if (result.data) {
          setCategories(result.data.categories || []);
        } else {
          setCategories([]);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load categories');
        setCategories([]);
      } finally {
        setLoadingCategories(false);
      }
    };

    loadCategories();
    
    // Load category suggestions
    loadSuggestions();
    
    // Load account information
    const loadAccount = async () => {
      if (!transaction?.accountId) return;
      setLoadingAccount(true);
      try {
        const result = await hybridAccountService.getAccounts({
          status: 'active'
        });
        if (result.data) {
          const foundAccount = result.data.accounts.find(acc => acc.id === transaction.accountId);
          setAccount(foundAccount || null);
        }
      } catch (err) {
        console.error('Failed to load account:', err);
        setAccount(null);
      } finally {
        setLoadingAccount(false);
      }
    };
    
    loadAccount();
    
    // Set initial selected category
    setSelectedCategoryId(transaction.categoryId || null);
    setSearchQuery('');
    setSuccess(false);
    // Set initial notes
    setNotes(transaction.notes || '');
  }, [isOpen, transaction, categoryType]);

  // Load category suggestions
  const loadSuggestions = async () => {
    if (!transaction) return;
    
    setLoadingSuggestions(true);
    try {
      const userId = await getCurrentUserId();
      if (!userId) {
        setSuggestions([]);
        return;
      }

      const result = await transactionService.getCategorySuggestions(
        {
          merchantName: transaction.merchantName,
          description: transaction.description
        },
        userId,
        3
      );

      if (result.error) {
        console.error('Failed to load suggestions:', result.error.message);
        setSuggestions([]);
      } else {
        setSuggestions(result.data || []);
      }
    } catch (err) {
      console.error('Error loading suggestions:', err);
      setSuggestions([]);
    } finally {
      setLoadingSuggestions(false);
    }
  };

  // Filter categories by search query and type
  const filteredCategories = useMemo(() => {
    let filtered = categories;
    
    // Filter by type
    if (typeFilter !== 'all') {
      filtered = filtered.filter(cat => cat.type === typeFilter);
    }
    
    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchingCategories = filtered.filter(cat => 
        cat.name.toLowerCase().includes(query) ||
        cat.note?.toLowerCase().includes(query)
      );
      
      // If searching, also include parents of matching children
      const matchingCategoryIds = new Set(matchingCategories.map(cat => cat.id));
      const parentIdsToInclude = new Set<string>();
      
      matchingCategories.forEach(cat => {
        if (cat.parentId) {
          parentIdsToInclude.add(cat.parentId);
        }
      });
      
      // Add parent categories that have matching children
      const parentsToInclude = filtered.filter(cat => 
        !cat.parentId && parentIdsToInclude.has(cat.id)
      );
      
      // Combine matching categories with their parents
      const allCategoriesToShow = [...matchingCategories];
      parentsToInclude.forEach(parent => {
        if (!matchingCategoryIds.has(parent.id)) {
          allCategoriesToShow.push(parent);
        }
      });
      
      return allCategoriesToShow;
    }
    
    return filtered;
  }, [categories, searchQuery, typeFilter]);

  // Organize categories hierarchically (parent/child)
  const organizedCategories = useMemo(() => {
    const topLevelCategories = filteredCategories.filter(cat => !cat.parentId);
    const buildTree = (parent: Category): (Category & { children?: Category[] }) => {
      const children = filteredCategories.filter(cat => cat.parentId === parent.id)
        .sort((a, b) => a.name.localeCompare(b.name))
        .map(buildTree);
      return { ...parent, children };
    };
    return topLevelCategories
      .sort((a, b) => a.name.localeCompare(b.name))
      .map(buildTree);
  }, [filteredCategories]);

  // Toggle category expansion
  const toggleCategoryExpand = (categoryId: string) => {
    const newExpanded = new Set(expandedCategoryIds);
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId);
    } else {
      newExpanded.add(categoryId);
    }
    setExpandedCategoryIds(newExpanded);
  };

  const handleCategorise = async () => {
    if (!transaction || selectedCategoryId === undefined) {
      setError('Please select a category or choose "Uncategorise".');
      return;
    }

    setError(null);
    setSuccess(false);

    try {
      // First, categorise the transaction
      await categoriseMutation.mutateAsync({
        transactionId: transaction.id,
        categoryId: selectedCategoryId
      });

      // Then, update notes if they've changed
      const notesChanged = notes !== (transaction.notes || '');
      if (notesChanged) {
        await updateMutation.mutateAsync({
          transactionId: transaction.id,
          updates: { notes: notes.trim() || undefined }
        });
      }

      setSuccess(true);
      onComplete();
      // Auto-close after 1.5 seconds
      setTimeout(() => {
        handleClose();
      }, 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to categorise transaction');
    }
  };

  const handleClose = () => {
    setSelectedCategoryId(undefined);
    setSearchQuery('');
    setError(null);
    setSuccess(false);
    setNotes('');
    onClose();
  };

  if (!isOpen || !transaction) return null;

  const isLoading = categoriseMutation.isPending || updateMutation.isPending;

  return (
    <div
      className="fixed inset-0 z-[70] overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-labelledby="categorise-modal-title"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          handleClose();
        }
      }}
    >
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" 
        onClick={handleClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="flex min-h-full items-start justify-center pt-14 pb-4 px-4">
        <div
          className="relative w-full max-w-5xl transform overflow-hidden rounded-lg bg-white shadow-xl transition-all dark:bg-gray-900"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-gray-700">
            <h2 id="categorise-modal-title" className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              Categorise Transaction
            </h2>
            <button
              onClick={handleClose}
              className="ml-4 p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              aria-label="Close categorise modal"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="max-h-[calc(100vh-12rem)] overflow-y-auto px-6 py-4">
            {/* Split Layout: 1/3 left, 2/3 right on desktop, stacked on mobile */}
            <div className="flex flex-col md:flex-row gap-6 h-full">
              {/* Left Side: Transaction Details (1/3 on desktop, full width on mobile) */}
              <div className="w-full md:w-1/3 flex-shrink-0">
                <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg md:sticky top-0 space-y-4">
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {transaction.merchantName || transaction.description}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      {new Date(transaction.date).toLocaleDateString()} • {transaction.type === 'debit' ? '-' : '+'}${transaction.amount.toFixed(2)}
                      {account && ` • ${account.name}`}
                    </p>
                  </div>
                  
                  {/* Notes Field */}
                  <div>
                    <label htmlFor="transaction-notes" className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                      Notes
                    </label>
                    <textarea
                      id="transaction-notes"
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Add notes..."
                      disabled={isLoading}
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed resize-none"
                      aria-label="Transaction notes"
                    />
                  </div>

                  {/* Current Category with Uncategorise - Show if transaction has a category */}
                  {transaction.categoryId && (() => {
                    const currentCategory = categories.find(cat => cat.id === transaction.categoryId);
                    if (currentCategory) {
                      const IconComponent = getCategoryIconComponent(currentCategory);
                      const isUncategoriseSelected = selectedCategoryId === null;
                      return (
                        <div>
                          <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                            Current Category
                          </label>
                          <div className={`flex items-center space-x-3 px-3 py-2.5 border-2 rounded-lg transition-all ${
                            isUncategoriseSelected
                              ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                              : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-700'
                          }`}>
                            {IconComponent && (
                              <div 
                                className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                                style={{ backgroundColor: currentCategory.color ? `${currentCategory.color}20` : undefined }}
                              >
                                <IconComponent 
                                  className="h-4 w-4" 
                                  style={{ color: currentCategory.color || undefined }}
                                />
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                                {currentCategory.name}
                              </p>
                            </div>
                            <button
                              onClick={() => setSelectedCategoryId(null)}
                              disabled={isLoading}
                              className={`ml-2 p-1.5 rounded-md transition-colors flex-shrink-0 ${
                                isUncategoriseSelected
                                  ? 'bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300'
                                  : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'
                              } disabled:opacity-50 disabled:cursor-not-allowed`}
                              aria-label="Remove category"
                              title="Remove category"
                            >
                              <X className="h-4 w-4" />
                            </button>
                            {isUncategoriseSelected && (
                              <CheckCircle className="h-4 w-4 text-primary-600 dark:text-primary-400 flex-shrink-0 ml-1" />
                            )}
                          </div>
                          {isUncategoriseSelected && (
                            <p className="text-xs text-primary-600 dark:text-primary-400 mt-1.5 ml-1">
                              Category will be removed when you categorise
                            </p>
                          )}
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>
              </div>

              {/* Right Side: Search and Categories (2/3 on desktop, full width on mobile) */}
              <div className="flex-1 min-w-0 w-full md:w-auto">

                {success ? (
                  <div className="border rounded-lg p-6 bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800">
                    <div className="flex items-start space-x-3">
                      <CheckCircle className="h-6 w-6 text-green-600 dark:text-green-400 mt-0.5" />
                      <div className="flex-1">
                        <h4 className="font-medium text-green-800 dark:text-green-200 mb-2">
                          Categorisation Complete
                        </h4>
                        <p className="text-sm text-green-700 dark:text-green-300">
                          Transaction categorised successfully
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Filter and Search */}
                    <div className="mb-6 space-y-3">
                      <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 border border-gray-200 dark:border-gray-600">
                        <div className="flex items-center gap-4 flex-wrap">
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap">Type:</span>
                          <div className="flex items-center gap-4 flex-wrap">
                            <label className="flex items-center space-x-2 cursor-pointer">
                              <input
                                type="radio"
                                name="categoryTypeFilter"
                                value="all"
                                checked={typeFilter === 'all'}
                                onChange={(e) => setTypeFilter(e.target.value as 'all' | CategoryType)}
                                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                              />
                              <span className={`text-sm ${typeFilter === 'all' ? 'font-medium text-gray-900 dark:text-gray-100' : 'text-gray-600 dark:text-gray-400'}`}>
                                All
                              </span>
                            </label>
                            <label className="flex items-center space-x-2 cursor-pointer">
                              <input
                                type="radio"
                                name="categoryTypeFilter"
                                value={CategoryType.EXPENSE}
                                checked={typeFilter === CategoryType.EXPENSE}
                                onChange={(e) => setTypeFilter(e.target.value as 'all' | CategoryType)}
                                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                              />
                              <span className={`text-sm ${typeFilter === CategoryType.EXPENSE ? 'font-medium text-gray-900 dark:text-gray-100' : 'text-gray-600 dark:text-gray-400'}`}>
                                Expense
                              </span>
                            </label>
                            <label className="flex items-center space-x-2 cursor-pointer">
                              <input
                                type="radio"
                                name="categoryTypeFilter"
                                value={CategoryType.INCOME}
                                checked={typeFilter === CategoryType.INCOME}
                                onChange={(e) => setTypeFilter(e.target.value as 'all' | CategoryType)}
                                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                              />
                              <span className={`text-sm ${typeFilter === CategoryType.INCOME ? 'font-medium text-gray-900 dark:text-gray-100' : 'text-gray-600 dark:text-gray-400'}`}>
                                Income
                              </span>
                            </label>
                            <label className="flex items-center space-x-2 cursor-pointer">
                              <input
                                type="radio"
                                name="categoryTypeFilter"
                                value={CategoryType.TRANSFER}
                                checked={typeFilter === CategoryType.TRANSFER}
                                onChange={(e) => setTypeFilter(e.target.value as 'all' | CategoryType)}
                                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                              />
                              <span className={`text-sm ${typeFilter === CategoryType.TRANSFER ? 'font-medium text-gray-900 dark:text-gray-100' : 'text-gray-600 dark:text-gray-400'}`}>
                                Transfer
                              </span>
                            </label>
                          </div>
                        </div>
                      </div>
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" aria-hidden="true" />
                        <input
                          type="text"
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          placeholder="Search categories..."
                          className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          aria-label="Search categories"
                        />
                      </div>
                    </div>

                    {/* Error Message */}
                    {error && (
                      <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                        <div className="flex items-start space-x-3">
                          <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 mt-0.5" />
                          <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
                        </div>
                      </div>
                    )}

                    {/* Loading Categories */}
                    {loadingCategories ? (
                      <div className="flex items-center justify-center py-12">
                        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
                        <span className="ml-3 text-gray-600 dark:text-gray-400">Loading categories...</span>
                      </div>
                    ) : (
                      <>
                        {/* Smart Suggestions */}
                    {suggestions.length > 0 && (
                      <div className="mb-6">
                        <div className="flex items-center space-x-2 mb-3">
                          <Sparkles className="h-4 w-4 text-primary-600 dark:text-primary-400" />
                          <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            Smart Suggestions
                          </h3>
                        </div>
                        <div className="space-y-2">
                          {suggestions.map((suggestion) => {
                            const category = categories.find(c => c.id === suggestion.categoryId);
                            const IconComponent = category ? getCategoryIconComponent(category) : null;
                            const isSelected = selectedCategoryId === suggestion.categoryId;

                            return (
                              <button
                                key={suggestion.categoryId}
                                onClick={() => setSelectedCategoryId(suggestion.categoryId)}
                                disabled={isLoading}
                                className={`w-full p-3 border-2 rounded-lg text-left transition-all ${
                                  isSelected
                                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                                    : 'border-primary-200 dark:border-primary-800 hover:border-primary-300 dark:hover:border-primary-700'
                                } disabled:opacity-50 disabled:cursor-not-allowed`}
                              >
                                <div className="flex items-center space-x-3">
                                  {IconComponent ? (
                                    <div
                                      className="w-8 h-8 rounded flex items-center justify-center flex-shrink-0"
                                      style={{
                                        backgroundColor: category?.color ? `${category.color}33` : undefined,
                                        color: category?.color || undefined
                                      }}
                                    >
                                      <IconComponent className="h-4 w-4" />
                                    </div>
                                  ) : (
                                    <div className="w-8 h-8 rounded bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center flex-shrink-0">
                                      <Sparkles className="h-4 w-4 text-primary-600 dark:text-primary-400" />
                                    </div>
                                  )}
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                                      {suggestion.categoryName}
                                    </p>
                                    <p className="text-xs text-gray-600 dark:text-gray-400">
                                      Used {suggestion.count} time{suggestion.count !== 1 ? 's' : ''} for similar transactions
                                    </p>
                                  </div>
                                  {isSelected && (
                                    <CheckCircle className="h-4 w-4 text-primary-600 dark:text-primary-400 flex-shrink-0" />
                                  )}
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}


                    {/* Categories List - Expandable */}
                    {organizedCategories.length === 0 ? (
                      <div className="text-center py-12">
                        <p className="text-gray-600 dark:text-gray-400">
                          {searchQuery ? 'No categories found matching your search' : 'No categories available'}
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-1 max-h-[calc(90vh-300px)] overflow-y-auto">
                        {organizedCategories.map((category) => {
                          const IconComponent = getCategoryIconComponent(category);
                          const isSelected = selectedCategoryId === category.id;
                          const hasChildren = category.children && category.children.length > 0;
                          const isExpanded = expandedCategoryIds.has(category.id);

                          // Get type badge color
                          const getTypeBadgeColor = () => {
                            if (category.type === CategoryType.INCOME) {
                              return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
                            }
                            if (category.type === CategoryType.TRANSFER) {
                              return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
                            }
                            return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
                          };

                          // Get icon background color
                          const getIconBackgroundColor = () => {
                            if (category.color) {
                              const hex = category.color.replace('#', '');
                              if (hex.length === 6) {
                                const r = parseInt(hex.substring(0, 2), 16);
                                const g = parseInt(hex.substring(2, 4), 16);
                                const b = parseInt(hex.substring(4, 6), 16);
                                if (!Number.isNaN(r) && !Number.isNaN(g) && !Number.isNaN(b)) {
                                  return `rgba(${r}, ${g}, ${b}, 0.12)`;
                                }
                              }
                            }
                            return 'rgba(99, 102, 241, 0.12)';
                          };

                          return (
                            <div key={category.id} className="border-b border-gray-100 dark:border-gray-700/50">
                              {/* Parent Category */}
                              <div className={`flex items-start sm:items-center gap-2 sm:gap-3 py-3 px-2 sm:px-4 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors ${
                                isSelected ? 'bg-primary-50 dark:bg-primary-900/20' : ''
                              }`}>
                                {/* Expand/Collapse Button */}
                                <button
                                  onClick={() => toggleCategoryExpand(category.id)}
                                  className={`flex-shrink-0 w-5 h-5 flex items-center justify-center transition-transform mt-0.5 sm:mt-0 ${
                                    hasChildren ? 'cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-600 rounded' : 'cursor-default opacity-0'
                                  }`}
                                  aria-label={isExpanded ? 'Collapse' : 'Expand'}
                                  aria-expanded={hasChildren ? isExpanded : undefined}
                                >
                                  {hasChildren && (
                                    isExpanded ? (
                                      <ChevronDown className="w-4 h-4 text-gray-600 dark:text-gray-400" aria-hidden="true" />
                                    ) : (
                                      <ChevronRight className="w-4 h-4 text-gray-600 dark:text-gray-400" aria-hidden="true" />
                                    )
                                  )}
                                </button>

                                {/* Category Button */}
                                <button
                                  onClick={() => setSelectedCategoryId(category.id)}
                                  disabled={isLoading}
                                  className="flex-1 flex items-start sm:items-center gap-2 sm:gap-3 text-left disabled:opacity-50 disabled:cursor-not-allowed min-w-0"
                                >
                                  {/* Category Icon */}
                                  <div
                                    className="flex-shrink-0 flex h-8 w-8 sm:h-10 sm:w-10 items-center justify-center rounded-lg"
                                    style={{ backgroundColor: getIconBackgroundColor() }}
                                  >
                                    {IconComponent ? (
                                      <IconComponent className="w-4 h-4 sm:w-5 sm:h-5" style={{ color: category.color || undefined }} aria-hidden="true" />
                                    ) : (
                                      <span className="text-xs sm:text-sm font-medium" style={{ color: category.color || undefined }}>
                                        {category.name.charAt(0)}
                                      </span>
                                    )}
                                  </div>

                                  {/* Category Info */}
                                  <div className="flex-1 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-4 min-w-0">
                                    <div className="flex-1 min-w-0">
                                      <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
                                        <span className="font-medium text-sm sm:text-base text-gray-900 dark:text-gray-100 break-words">
                                          {category.name}
                                        </span>
                                        {hasChildren && (
                                          <span className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">
                                            {category.children!.length} {category.children!.length === 1 ? 'subcategory' : 'subcategories'}
                                          </span>
                                        )}
                                        <span className={`text-xs px-1.5 sm:px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${getTypeBadgeColor()}`}>
                                          {category.type === CategoryType.INCOME ? 'Income' : category.type === CategoryType.TRANSFER ? 'Transfer' : 'Expense'}
                                        </span>
                                        {category.isSystem && (
                                          <span className="text-xs px-1.5 sm:px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded whitespace-nowrap">
                                            System
                                          </span>
                                        )}
                                      </div>
                                      {category.note && (
                                        <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-0.5 sm:mt-1 break-words">
                                          {category.note}
                                        </p>
                                      )}
                                    </div>
                                    {isSelected && (
                                      <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-primary-600 dark:text-primary-400 flex-shrink-0 self-start sm:self-center" />
                                    )}
                                  </div>
                                </button>
                              </div>

                              {/* Child Categories */}
                              {hasChildren && isExpanded && (
                                <div className="bg-gray-50 dark:bg-gray-800/50">
                                  {category.children!.map((child) => {
                                    const ChildIconComponent = getCategoryIconComponent(child);
                                    const isChildSelected = selectedCategoryId === child.id;

                                    const getChildIconBackgroundColor = () => {
                                      if (child.color) {
                                        const hex = child.color.replace('#', '');
                                        if (hex.length === 6) {
                                          const r = parseInt(hex.substring(0, 2), 16);
                                          const g = parseInt(hex.substring(2, 4), 16);
                                          const b = parseInt(hex.substring(4, 6), 16);
                                          if (!Number.isNaN(r) && !Number.isNaN(g) && !Number.isNaN(b)) {
                                            return `rgba(${r}, ${g}, ${b}, 0.12)`;
                                          }
                                        }
                                      }
                                      return 'rgba(99, 102, 241, 0.12)';
                                    };

                                    return (
                                      <button
                                        key={child.id}
                                        onClick={() => setSelectedCategoryId(child.id)}
                                        disabled={isLoading}
                                        className={`w-full flex items-start sm:items-center gap-2 sm:gap-3 py-2 px-2 sm:px-4 pl-8 sm:pl-12 hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-left ${
                                          isChildSelected ? 'bg-primary-50 dark:bg-primary-900/20' : ''
                                        }`}
                                      >
                                        {/* Child Icon */}
                                        <div
                                          className="flex-shrink-0 flex h-7 w-7 sm:h-8 sm:w-8 items-center justify-center rounded-lg"
                                          style={{ backgroundColor: getChildIconBackgroundColor() }}
                                        >
                                          {ChildIconComponent ? (
                                            <ChildIconComponent className="w-3.5 h-3.5 sm:w-4 sm:h-4" style={{ color: child.color || undefined }} aria-hidden="true" />
                                          ) : (
                                            <span className="text-xs font-medium" style={{ color: child.color || undefined }}>
                                              {child.name.charAt(0)}
                                            </span>
                                          )}
                                        </div>

                                        {/* Child Info */}
                                        <div className="flex-1 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-4 min-w-0">
                                          <div className="flex-1 min-w-0">
                                            <span className="font-medium text-xs sm:text-sm text-gray-900 dark:text-gray-100 break-words">
                                              {child.name}
                                            </span>
                                            {child.note && (
                                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 break-words">
                                                {child.note}
                                              </p>
                                            )}
                                          </div>
                                          {isChildSelected && (
                                            <CheckCircle className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-primary-600 dark:text-primary-400 flex-shrink-0 self-start sm:self-center" />
                                          )}
                                        </div>
                                      </button>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          {!success && (
            <div className="flex items-center justify-between border-t border-gray-200 px-6 py-4 dark:border-gray-700">
              <div></div>
              <div className="flex justify-end space-x-3">
                <button
                onClick={handleClose}
                disabled={isLoading}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleCategorise}
                disabled={isLoading || selectedCategoryId === undefined}
                className="px-6 py-2 bg-primary-600 hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors flex items-center space-x-2"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    <span>Categorising...</span>
                  </>
                ) : (
                  <span>Categorise</span>
                )}
              </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

